import { useCallback, useEffect, useRef, useState } from "react";
import jspPlatformApi from "client/portals";
import { FoodItemKey } from "client/jspPlatformExperiment";

export interface SupplierCodeConflict {
  foodId: number;
  foodName: string;
}

export type SupplierCodeCheckStatus =
  | "idle"
  | "checking"
  | "clear"
  | "conflict";

export interface UseSupplierCodeDuplicateCheckResult {
  status: SupplierCodeCheckStatus;
  conflicts: SupplierCodeConflict[];
  runCheck: (productCode: string) => void;
  reset: () => void;
}

const toConflict = (match: FoodItemKey): SupplierCodeConflict => ({
  foodId: match.id,
  foodName: match.food_name ?? `Ingredient #${match.id}`,
});

/**
 * Warns when an entered supplier product code is already attached to another
 * active ingredient. The backend lookup endpoint handles inactive-ingredient
 * exclusion and equivalent-ingredient visibility — the hook just relays.
 */
const useSupplierCodeDuplicateCheck = (
  excludeFoodId?: number,
): UseSupplierCodeDuplicateCheckResult => {
  const [status, setStatus] = useState<SupplierCodeCheckStatus>("idle");
  const [conflicts, setConflicts] = useState<SupplierCodeConflict[]>([]);
  const abortRef = useRef<AbortController | null>(null);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setStatus("idle");
    setConflicts([]);
  }, []);

  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    [],
  );

  const runCheck = useCallback(
    (rawProductCode: string) => {
      const productCode = rawProductCode.trim();
      if (!productCode) {
        reset();
        return;
      }

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setStatus("checking");

      jspPlatformApi()
        .ingredient.getFoodSupplierInfoLookup({
          query: {
            product_code: productCode,
            exclude_food_id: excludeFoodId,
          },
          signal: controller.signal,
        })
        .then((res) => {
          if (controller.signal.aborted) {
            return;
          }
          const matches: FoodItemKey[] = res.data ?? [];
          const next = matches.map(toConflict);
          setConflicts(next);
          setStatus(next.length > 0 ? "conflict" : "clear");
        })
        .catch((error: unknown) => {
          if (controller.signal.aborted) {
            return;
          }
          // Treat any non-abort error as inconclusive — clear the warning
          // rather than leaving a stale state, but log so it's discoverable.
          // eslint-disable-next-line no-console
          console.warn("Supplier product code duplicate check failed", error);
          setConflicts([]);
          setStatus("idle");
        });
    },
    [excludeFoodId, reset],
  );

  return { status, conflicts, runCheck, reset };
};

export default useSupplierCodeDuplicateCheck;
